import React, { FC, ReactElement, createContext, useCallback, useMemo, useState } from 'react'
import { DialogContextType } from 'src/enums'
import { AlertDialog, AlertDialogCore, DialogContainer, ShowDialogParams, ShowDialogProps } from 'src/show-dialog'
import { generateUUID } from 'src/utils/function'
import { WarningDialog } from 'src/components/WarningDialog'
import { ShowFileDialog } from 'src/dialogs/ShowFileDialog'
import { ShowImageDialog } from 'src/dialogs/ShowImageDialog'
import { SxProps, Theme } from '@mui/material'

export interface UIProps {
  Icon?: React.ReactElement
  iconBGColor?: string
  buttonTextColor?: string
  buttonBGColor?: string
}

export interface ButtonConfig {
  text: string
  variant?: 'contained' | 'outlined' | 'text'
  sx?: SxProps<Theme>
  callback: (pop) => void
}

export interface DialogContextProps {
  showDialog: (Content: any, options: any, dialogContextType?: DialogContextType, isWithLayout?: boolean) => any
  dialogsCustom: any
  showConfirmDialog: (
    title: string,
    text?: string | ReactElement,
    buttonTexts?: { acceptText?: string | undefined; cancelText?: string | undefined },
    onConfirm?: (callback: any) => void,
    UIProps?: UIProps,
    isMinimal?: boolean,
    onCancel?: (callback: any) => void,
  ) => any
  showWarningDialog: (
    title: string,
    description?: string,
    isCancelButtonShow?: boolean,
    buttons?: ButtonConfig[],
    variant?: 'warning' | 'info',
  ) => any
  openFilePreviewDialog: (
    fileUrl: string,
    title?: string,
    bottomAction?: (pop: () => void) => React.ReactNode,
    fileType?: string,
  ) => any
  openImagePreviewDialog: (
    fileUrl: string,
    alttext?: string,
    title?: string,
    aspectRatio?: string,
    height?: string,
    onReplace?: (params: any) => string | void,
    onDelete?: () => void,
  ) => any
  closeAllDialogs: () => void
}

interface DialogContextParams<T> extends ShowDialogParams<T> {
  title?: string
}

export const DialogContextCtx = createContext<DialogContextProps>(null!)

export const DialogContextProvider = ({ children }) => {
  const [dialogs, setDialogs] = useState({})
  const [dialogsCustom, setDialogCustom] = useState({})

  const closeDialog = useCallback((data, id, pop, contextType = null) => {
    if (pop) {
      pop(data)
    }
    if (contextType) {
      setDialogCustom(prev => {
        const contextDialogs = prev[contextType]
        if (!contextDialogs?.[id]) return prev
        const nextContextDialogs = { ...contextDialogs }
        delete nextContextDialogs[id]
        return { ...prev, [contextType]: nextContextDialogs }
      })
    } else {
      setDialogs(prev => {
        if (!prev[id]) return prev
        const next = { ...prev }
        delete next[id]
        return next
      })
    }
  }, [])

  const closeAllDialogs = useCallback(() => {
    setDialogs({})
    setDialogCustom({})
  }, [])

  const showDialog = useCallback(
    function showDialogfunc<T = any, K = any>(
      Content: FC<K & ShowDialogProps<T>>,
      options: DialogContextParams<K> = {},
      contextType?: DialogContextType,
      isWithLayout?: boolean,
    ) {
      const { opts, componentProps, pop, isAutoCloseDisabled, title, sx } = options
      if (contextType) {
        const idVal = `custom-${generateUUID()}`
        setDialogCustom(d => ({
          ...d,
          ...{
            [contextType]: {
              ...d[contextType],
              ...{
                [idVal]: (
                  <DialogContainer
                    pop={data => closeDialog(data, idVal, pop, contextType)}
                    content={Content}
                    key={idVal}
                    opts={opts}
                    contentProps={componentProps as any}
                    isAutoCloseDisabled={isAutoCloseDisabled}
                    title={title}
                    isWithLayout={isWithLayout}
                    sx={sx}
                  />
                ),
              },
            },
          },
        }))
      } else {
        const id = `locality-${generateUUID()}`
        setDialogs(prevDialog => {
          return {
            ...prevDialog,
            ...{
              [id]: (
                <DialogContainer
                  pop={data => closeDialog(data, id, pop)}
                  content={Content}
                  opts={opts}
                  contentProps={componentProps as any}
                  isAutoCloseDisabled={isAutoCloseDisabled}
                  title={title}
                  isWithLayout={isWithLayout}
                  sx={sx}
                />
              ),
            },
          }
        })
      }
    },
    [closeDialog],
  )

  function showConfirmDialog(
    title: string,
    text?: string | ReactElement,
    {
      acceptText = undefined,
      cancelText = undefined,
    }: { acceptText?: string | undefined; cancelText?: string | undefined } = {},
    onConfirm?: (callback: any) => void,
    UIProps?: UIProps,
    isMinimal?: boolean,
    onCancel?: (callback: any) => void,
  ) {
    if (UIProps) {
      showDialog(
        AlertDialogCore,
        {
          componentProps: {
            title: title,
            acceptText,
            cancelText,
            text: text,
            onConfirm: onConfirm,
            onCancel: onCancel,
            UIProperties: UIProps,
            isMinimal: isMinimal || false,
          },
          opts: { maxWidth: 'xs', fullWidth: true },
        },
        undefined,
        isMinimal || false,
      )
    } else {
      showDialog(AlertDialog, {
        componentProps: {
          title: title,
          acceptText,
          cancelText,
          text: text,
          onConfirm: onConfirm,
          isMinimal: isMinimal || false,
        },
      })
    }
  }

  function showWarningDialog(
    title: string,
    description?: string,
    isCancelButtonShow = true,
    buttons?: ButtonConfig[],
    variant?: 'warning' | 'info',
  ) {
    showDialog(
      WarningDialog,
      {
        componentProps: {
          title: title,
          description: description,
          isCancelButtonShow,
          buttons,
          variant,
        } as any,
      },
      undefined,
      true,
    )
  }

  const openFilePreviewDialog = useCallback(
    async function openFilePreviewDialog(
      fileUrl: string,
      title?: string,
      bottomAction?: (pop: () => void) => React.ReactNode,
      fileType?: string,
    ) {
      await showDialog(
        ShowFileDialog,
        {
          opts: {
            maxWidth: '80vw',
            height: '80vh',
          } as any,
          componentProps: {
            fileUrl: fileUrl,
            title: title,
            bottomAction: bottomAction,
            fileType: fileType,
          },
        },
        undefined,
        true,
      )
    },
    [showDialog],
  )

  const openImagePreviewDialog = useCallback(
    async function openImagePreviewDialog(
      fileUrl: string,
      alttext?: string,
      title?: string,
      aspectRatio?: string,
      height?: string,
      onReplace?: (params: any) => string | void,
      onDelete?: () => void,
    ) {
      await showDialog(
        ShowImageDialog as any,
        {
          opts: {
            aspectRatio: aspectRatio ?? '3/2',
            height: height ?? '60vh',
            width: 'auto',
          } as any,
          componentProps: {
            fileUrl: fileUrl,
            altText: alttext,
            title: title,
            onReplace: onReplace,
            onDelete: onDelete,
          } as any,
        },
        undefined,
        true,
      )
    },
    [showDialog],
  )
  const contextValue: DialogContextProps = useMemo(
    () => ({
      showDialog,
      showConfirmDialog,
      showWarningDialog,
      openFilePreviewDialog,
      openImagePreviewDialog,
      dialogsCustom: { ...dialogsCustom },
      closeAllDialogs,
    }),
    [
      showDialog,
      showConfirmDialog,
      showWarningDialog,
      openFilePreviewDialog,
      openImagePreviewDialog,
      dialogsCustom,
      closeAllDialogs,
    ],
  )

  return (
    <DialogContextCtx.Provider value={contextValue}>
      {Object.values(dialogs)}
      {children}
    </DialogContextCtx.Provider>
  )
}
